import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import raf from 'raf';
import * as TWEEN from '@tweenjs/tween.js';

import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';

import { MapInteraction } from 'react-map-interaction';
import { extent } from 'd3-array';
import { nest } from 'd3-collection';
import { scaleLinear } from 'd3-scale';
import { line, curveLinearClosed } from 'd3-shape';
import { feature, merge } from 'topojson';

import sortBy from 'lodash.sortby';
import debounce from 'lodash.debounce';
import cloneDeep from 'lodash.clonedeep';

import DataLoader from './DataLoader';
import Gradients from './gradients.js';
import Filters from './Filters.js';
import Stacks from './Stacks.js';
import Tooltip from './Tooltip';
import Chart from './Chart.js';
import Map from './Map.js';

import { metrics, colors, rotations, section, range, locations, capitals, sectionsByGrouping } from './settings.js';

import scaleProjection from './scaleProjection.js';

import us from './us.30000.20m.topo.json';
import './App.css';

const clamp = (min, value, max) => Math.max(min, Math.min(value, max));

class App extends Component {
  constructor(props) {
    super(props);

    this.init = {};
    this.data = {
      state: [],
      city: [],
    };
    this.visible = [];

    this.state = {
      filters: {},
      level: 'state',
      group: 'Type',
      rotation: 'west',
      chart: {
        type: 'state',
        geo: 'United States',
      },
      date: {
        min: 0,
        max: 1,
      },
      width: window.innerWidth - metrics.padding * 2,
      height: window.innerHeight - metrics.chartHeight,
      scale: metrics.scale.min,
      translation: {
        x: 0,
        y: 0,
      },
    };

    this._recomputePaths = true;

    this.keyFunction = (this.state.group === 'Group') ? this.groupByGroup : this.groupByType;

    this.state.width = this.state.width >= metrics.width ? this.state.width : metrics.width;
    if (this.state.height <= metrics.height) {
      this.state.height = metrics.height;
    } else if (this.state.height >= this.state.width / metrics.aspectRatio) {
      this.state.height = this.state.width / metrics.aspectRatio;
    }

    if (this.state.width > window.innerWidth) {
      this.globalScale = window.innerWidth / this.state.width;
    } else {
      this.globalScale = 1;
    }

    this.container = null;
    this.stacks = {
      refs: {
        state: {},
        city: {},
      },
      update: false,
      ready: false,
    };

    this.map = {
      states: feature(us, us.objects.states),
      outline: merge(us, us.objects.states.geometries),
    };

    const scaled = scaleProjection(this.map.states, this.state.width, this.state.height);
    this.path = scaled.path;
    this.projection = scaled.projection;

    this.capitals = capitals.map(c => {
      c.projected = this.projection([c.Longitude, c.Latitude]);
      return c;
    });

    this.isoBar = line()
      .x(d => d.x)
      .y(d => d.y)
      .curve(curveLinearClosed);

    this.yScales = {};
    metrics.levels.forEach(l => {
      this.yScales[l] = scaleLinear()
        .range([0, this.state.height / 3]);
    });

    this.initTransform = null;
    const params = new URLSearchParams(window.location.search);
    const hasInitTransform = (params.has('s') && params.has('x') && params.has('y'));
    if (hasInitTransform) {
      this.initTransform = {
        scale: params.get('s') * 1,
        translation: {
          x: params.get('x') * this.state.width,
          y: params.get('y') * this.state.height,
        },
      };
    } else {
      this.initTransform = {
        scale: 1,
        translation: {
          x: -102, // prevent overlap w/ filters on right
          y: 32, // prevent overlap w/ date select at top
        },
      };
    }

  }

  groupByType(g) {
    return `${g['Flier Type']}-${g['Location Type']}`;
  }

  getCleanType = (g) => {
    const cleanTypeMap = {
      'banner-Campus': 'Banners',
      'banner-Public': 'Banners',
      'flier-Campus': 'Campus Flyers',
      'flier-Public': 'Public Flyers',
    };
    return cleanTypeMap[this.groupByType(g)];
  }

  groupByGroup = (g) => {
    const groups = this.groups.Group.top.map(t => t.name);
    if (groups.includes(g.Group)) {
      return g.Group;
    }
    return 'Other';
  }

  updateDimensions = () => {
    let width = window.innerWidth - metrics.padding * 2;
    width = width >= metrics.width ? width : metrics.width;
    let height = window.innerHeight - metrics.chartHeight;
    if (height <= metrics.height) {
      height = metrics.height;
    } else if (height >= width / metrics.aspectRatio) {
      height = width / metrics.aspectRatio;
    }

    let globalScale = 1;
    if (width > window.innerWidth) {
      globalScale = window.innerWidth / width;
    }

    if (
        width === this.state.width
          &&
        height === this.state.height
          &&
        globalScale === this.globalScale
      ) {
      return;
    }

    this.globalScale = globalScale;

    const scaled = scaleProjection(this.map.states, width, height);
    this.path = scaled.path;
    this.projection = scaled.projection;

    this.capitals = capitals.map(c => {
      c.projected = this.projection([c.Longitude, c.Latitude]);
      return c;
    });

    const lastTransform = Object.assign({}, {
        scale: this.state.scale,
        translation: this.state.translation,
      });
    const init = this.init;
    metrics.levels.forEach(l => {
      this.yScales[l].range([0, height / 3]);
      init[l] = this.projectPoints(init[l]);
    });

    this.filterData();
    this.stacks.ready = false;
    this._recomputePaths = true;
    this.setState({
        width,
        height,
        scale: metrics.scale.min,
        translation: { x: 0, y: 0 }
      },
      () => {
        this.updateStackPositions();
        this.setState(lastTransform);
      }
    );
  }

  componentDidMount() {
    window.addEventListener('resize',
      debounce(this.updateDimensions, 150, { leading: false, trailing: true })
    );
    DataLoader.load().then(response => {
      const filters = {};
      this.groups = response.groups;
      ['Group', 'Type'].forEach(g => {
        filters[g] = {};
        this.groups[g].top.forEach(t => {
          filters[g][t.name] = t;
        });
      });

      const dateExtent = extent(response.data.map(e => {
        return new Date(e.datePieces[2], e.datePieces[0] - 1);
      }));
      this.dateModified = new Date(Math.max(...response.data.map(d => d.date))).toLocaleDateString();

      this.dateRange = [];
      let index = 0;
      let year = dateExtent[0].getFullYear();
      let month = dateExtent[0].getMonth();
      let currentDate = new Date(year, month);
      while (currentDate < dateExtent[1]) {
        currentDate = new Date(year, month);
        this.dateRange.push({
          index,
          date: currentDate,
          month: currentDate.getMonth() + 1,
          year: currentDate.getFullYear(),
          value: currentDate.valueOf(),
        });
        month++;
        index++;
      }

      this.baseMarks = {};
      // mark all januarys w/ small line
      this.dateRange.filter(d => d.month === 1).forEach(d => {
        this.baseMarks[d.index] = <div style={range.marks.january}>|</div>;
      });
      // mark years at center
      const years = [...new Set(this.dateRange.map(d => d.year))];
      years.forEach(y => {
        const thisRange = this.dateRange.filter(d => d.year === y);
        const months = thisRange.map(d => d.month);
        const min = Math.min(...months);
        const max = Math.max(...months) + 1;
        const rawOffset = (max - min) / 2;
        const offset = Math.floor(rawOffset);
        if (offset > 0) { // need enough room for the label
          const style = Object.assign({}, range.marks.year);
          style.marginLeft = offset > 2 ? 0 : metrics.padding;
          const index = thisRange.find(r => r.month === (min + offset)).index;
          this.baseMarks[index] = <div style={style}>{y}</div>;
        }
      });

      const date = this.setupDateMarks([0, (this.dateRange.length - 1)]);

      // assumes metrics.levels has 'state' first which it does.
      if (metrics.levels[0] !== 'state') {
        console.warn('metrics.levels seems out of order, city animations will be incorrect')
      }

      metrics.levels.forEach(l => {
        this.data[l] = this.formatData(response.data, l);
        // check for missing location (state)
        this.data[l] = this.data[l].filter(datum => {
          const hasLocation = datum.location;
          if (!hasLocation) {
            console.warn(`
              Missing Location, make sure to use the full state name: 
              ${datum.key} | ${datum.level}
            `);
          }
          return hasLocation;
        });
        const countExtent = extent(this.data[l].map(d => d.count));
        this.yScales[l].domain([0, countExtent[1]]);
        this.data[l] = this.formatGroups(this.data[l], l);
        if (l === 'city') {
          this.data[l].forEach(city => {
            const state = city.values[0].State;
            const matchingState = this.data.state.find(s => s.key === state);
            city.matchingState = matchingState
          })
        }
        this.data[l] = this.projectPoints(this.data[l]);
      });
      this.init = Object.assign({}, this.data);
      this.setState({ filters, date });
    });
    this._raf = raf(this._animate)
  }

  componentWillUnmount() {
    raf.cancel(this._raf)
  }

  _animate = () => {
    TWEEN.update()
    this._raf = raf(this._animate)
  }

  setupDateMarks = (values) => {
    const date = {
      min: values[0],
      max: values[1],
    };
    this.marks = Object.assign({}, this.baseMarks);
    this.marks[date.min] = (
        <div>
          {this.baseMarks[date.min] ? this.baseMarks[date.min] : ''}
          <div style={range.marks.left}>
            {this.dateRange[date.min].date.toLocaleDateString('en-US', { month: 'numeric', year: 'numeric' })}
          </div>
        </div>
      );
    this.marks[date.max] = (
        <div>
          {this.baseMarks[date.max] ? this.baseMarks[date.max] : ''}
          <div style={range.marks.right}>
            {this.dateRange[date.max].date.toLocaleDateString('en-US', { month: 'numeric', year: 'numeric' })}
          </div>
        </div>
      );
    return date;
  }

  formatData(data, level) {
    return nest()
      .key(d => {
        if (level === 'state') {
          return d.State;
        } else {
          return `${d.Latitude}-${d.Longitude}`
        }
      })
      .entries(data)
      .map(d => {
        d.count = d.values.length;
        d.level = level;
        if (level === 'state') {
          d.location = locations[d.key];
        } else {
          d.location = {
            Latitude: d.values[0].Latitude,
            Longitude: d.values[0].Longitude,
          };
        }
        return d;
      });
  }

  formatGroups(data, level) {
    const groups = data.map(d => {
      let yOffset = 0;
      d.groups = sortBy(
          nest()
            .key(this.keyFunction)
            .entries(d.values)
          , g => section(g.key).order)

      const sections = sectionsByGrouping[this.state.group];
      if (sections.length !== d.groups.length) {
        sections.forEach(section => {
          const exists = d.groups.find(g => g.key === section)
          if (!exists) {
            d.groups.push({ key: section, values: [] })
          }
        })
      }

      d.groups = sortBy(d.groups, g => section(g.key).order)

      d.groups.forEach((g, i) => {
          g.settings = section(g.key);
          if (level === 'state') {
            g.fillType = 'gradient';
            g.count = g.values.length;
            if (g.count > 0) {
              const nextHeight = this.yScales[level](g.count);
              g.path = this.generateIsoBar(
                metrics.barSize,
                nextHeight,
                yOffset,
                false,
              );
              yOffset += nextHeight;
            }
          } else {
            g.values.map(v => {
              v.fillType = 'color';
              const nextHeight = this.yScales[level](0.15);
              const nextOffset = this.yScales[level](0.5);
              v.path = this.generateIsoBar(
                metrics.barSize,
                nextHeight,
                yOffset,
                true,
              );
              yOffset += nextOffset;
              return v;
            });
          }
        });
      if (d.groups.length && level === 'state') {
        let capSettings = null
        d.groups.forEach(g => {
          if (g.count > 0) {
            capSettings = g.settings
          }
        })
        if (capSettings != null) {
          d.groups.push({
            key: 'cap',
            count: 0,
            settings: capSettings,
            fillType: 'color',
            path: this.generateIsoBar(
              metrics.barSize,
              this.yScales[level](0.15),
              yOffset,
              true,
            ),
          });
        }
      }
      return d;
    });
    return groups
  }

  getGroupTopCount = data => {
    ['Group', 'Type'].forEach(g => {
      this.groups[g].top = this.groups[g].top.map(group => {
        const keyFunction = g === 'Group' ? this.groupByGroup : this.getCleanType;
        group.count = data
                      .map(s => s.values)
                      .flat()
                      .filter(v => group.name === keyFunction(v))
                      .length;
        return group;
      });
    });
    return this.groups;
  }

  filterData = () => {
    const max = this.dateRange[this.state.date.max];
    const maxDate = new Date(max.year, max.month, 0).valueOf();
    metrics.levels.forEach(l => {
      const levelData = this.init[l].slice().map(d => {
        const stack = Object.assign({}, d);
        stack.values = stack.values.filter(v => {
          let include = true;
          if (v.date < this.dateRange[this.state.date.min].value || v.date > maxDate) {
            include = false;
          }
          ['Group', 'Type'].forEach(g => {
            const keyFunction = g === 'Group' ? this.groupByGroup : this.getCleanType;
            const [group] = this.groups[g].top.filter(t => t.name === keyFunction(v));
            if (!group.selected) {
              include = false;
            }
          });
          return include;
        });
        return stack;
      });
      if (l === 'state') {
        this.groups = this.getGroupTopCount(levelData);
      }
      this.data[l] = sortBy(this.formatGroups(levelData, l), d => d.screen.y);
    });
    this.visible = this.getVisibleStacks(
      this.data,
      this.state.scale,
      this.state.translation,
      this.state.level,
    );
  }

  swapGroup = (group) => {
    this.keyFunction = (group === 'Group') ? this.groupByGroup : this.groupByType;
    this.filterData();
    this.setState({ group });
  }

  toggleFilter = (group, value) => {
    const filters = cloneDeep(this.state.filters);
    value.selected = !value.selected;
    filters[group][value.name] = value;
    this.filterData();
    this.setState({ filters });
  }

  setChartType = (type) => {
    const chart = cloneDeep(this.state.chart);
    chart.type = type;
    this.setState({ chart });
  }

  setChartGeo = (geo) => {
    const chart = cloneDeep(this.state.chart);
    if (geo === chart.geo) {
      chart.geo = 'United States'
      chart.type = 'state'
    } else {
      chart.geo = geo;
    }
    if (geo !== 'United States') {
      chart.type = 'time';
    }
    this.setState({ chart });
  }

  getVisibleStacks(data, scale, translation, level) {
    return data[level];
  }

  projectPoints(data) {
    this.stacks.update = true;
    return data.map(d => {
      d.projected = this.projection([d.location.Longitude, d.location.Latitude]);
      return d;
    });
  }

  screenPoints(data, level) {
    const origin = ReactDOM.findDOMNode(this.container).getBoundingClientRect();
    return sortBy(data.map(d => {
      const location = ReactDOM
        .findDOMNode(this.stacks.refs[level][d.key])
        .getBoundingClientRect();
      d.screen = {
        x: (location.x - origin.x) / this.globalScale,
        y: (location.y - origin.y) / this.globalScale,
      };
      return d;
    }), d => d.screen.y);
  }

  generateIsoBar(width, height, yOffset, cap) {
    const pos = {
      x: 0,
      y: -(height + yOffset),
    };
    const iso = {
      x: width / 6,
      y: width / metrics.denom,
    };
    let capOffset = 0;
    if (cap) {
      capOffset = -(iso.y / 2);
    }
    const hexagon = [
      { x: pos.x + -iso.x, y: pos.y + height + iso.y + capOffset, }, // bottom point
      { x: pos.x + (width / 2), y: pos.y + height + (iso.y / 2) + capOffset, }, // bottom right
      { x: pos.x + (width / 2), y: pos.y, }, // top right
      { x: pos.x + iso.x, y: pos.y + -iso.y, }, // top point
      { x: pos.x + (-width / 2), y: pos.y - (iso.y / 2), }, // top left
      { x: pos.x + (-width / 2), y: pos.y + height + capOffset, }, // bottom left
      { x: pos.x + -iso.x, y: pos.y + height + iso.y + capOffset, }, // bottom point
    ];
    return this.isoBar(hexagon);
  }

  updateStackPositions = () => {
    this.stacks.update = false;
    metrics.levels.forEach(l => {
      this.data[l] = this.screenPoints(this.data[l], l);
      this.init[l] = this.screenPoints(this.init[l], l);
    });
    this.stacks.ready = true;
    this.visible = this.getVisibleStacks(
      this.data,
      this.state.scale,
      this.state.translation,
      this.state.level
    );
  }

  getTransformUrl() {
    const { scale, translation } = this.transform;
    const params = new URLSearchParams();
    params.set('s', scale);
    params.set('x', translation.x / this.state.width);
    params.set('y', translation.y / this.state.height);
    window.history.replaceState(null, null, `?${params.toString()}`);
    // http://localhost:3000/?s=10.5&x=-6.445321469575201&y=-3.470128634018238
    return window.location;
  }

  instantTransform = () => {
    const { scale, translation } = this.transform;
    const level = (scale < metrics.levelLine) ? metrics.levels[0] : metrics.levels[1];
    this.visible = this.getVisibleStacks(this.data, scale, translation, level);
    this.zooming = false;
    const tooltip = null;
    this.setState({ scale, translation, level, tooltip });
  }

  updateTransform = debounce(this.instantTransform, 150, {leading: false, trailing: true});

  handleTransform = (t, instant = false) => {
    this.zooming = true;
    this.transform = t;
    if (instant) {
      this.instantTransform();
    } else {
      this.updateTransform();
    }
  }

  componentDidUpdate() {
    if (this.stacks.update) {
      this.updateStackPositions();
      const callback = this.initTransform ? (
          this.handleTransform(this.initTransform, true)
        ) : null;
      const group = this.state.group;
      this.setState({ group }, callback);
    }
  }

  _openTooltip = (d, l) => {
    return (e) => {
      if (this.zooming) {
        return;
      }
      d.leaflet = l;
      const page = { x: 0, y: 0 };
      if (e.type === 'touchend') {
        const t = e.changedTouches[0];
        page.x = t.pageX;
        page.y = t.pageY;
      } else {
        page.x = e.pageX;
        page.y = e.pageY;
      }
      const containerDimension = this.container.getBoundingClientRect()
      this.setState({ tooltip: Object.assign({
        x: page.x - containerDimension.left,
        y: page.y - containerDimension.top,
      }, d)});
    }
  }

  _closeTooltip = () => {
    if (this.zooming) {
      return;
    }
    this.setState({ tooltip: null });
  }

  _openDateTooltip = (timeseries, dateScale, bottomChart) => {
    return (e) => {
      const x = e.clientX - metrics.padding;
      const offset = bottomChart.getBoundingClientRect();
      const date = dateScale.invert((x - offset.left) / this.globalScale)
      let closestDatum = null;
      let closestDatumDiff = Number.MAX_VALUE;
      timeseries[0].forEach(datum => {
        if (closestDatum === null) {
          const diff = Math.abs(date - datum.data.date);
          closestDatum = datum;
          closestDatumDiff = diff;
        } else {
          const diff = Math.abs(date - datum.data.date);
          if (diff < closestDatumDiff) {
            closestDatumDiff = diff;
            closestDatum = datum;
          }
        }
      })
      const containerDimension = this.container.getBoundingClientRect()
      this.setState({
        tooltip: {
          x: e.pageX - containerDimension.left,
          y: e.pageY - containerDimension.top,
          key: closestDatum.data.date,
          values: closestDatum.data.values,
          level: 'date',
          bumpUp: true,
        }
      })
    }
  }

  render() {
    if (!this.data[this.state.level].length) {
      return null;
    }
    return (
      <div
        className="App"
        style={{
          transformOrigin: '0 0 ',
          transform: `scale(${this.globalScale})`,
        }}
      >
        <div
          ref={r => this.container = r}
          className='container'
          style={{
            background: colors.grey.light,
            width: this.state.width,
            height: this.state.height,
            marginBottom: 0,
          }}
        >
          <div className='dateRange'>
            <Range
              dots
              min={0}
              max={(this.dateRange.length - 1)}
              step={1}
              value={[this.state.date.min, this.state.date.max]}
              marks={this.marks}
              railStyle={range.rail}
              trackStyle={[range.track]}
              handleStyle={[range.handle, range.handle]}
              allowCross={false}
              onChange={(values) => {
                const date = this.setupDateMarks(values);
                this.setState({ date });
              }}
              onAfterChange={(values) => {
                const date = this.setupDateMarks(values);
                this.filterData();
                this.setState({ date });
              }}
            />
          </div>
          <div className='dateModified'>
            Last Update: {this.dateModified}
          </div>
          <Filters
            group={this.state.group}
            swapGroup={this.swapGroup}
            filters={this.state.filters}
            groups={this.groups}
            toggleFilter={this.toggleFilter}
          />

          <MapInteraction
            ref={c => (this._mapInteractionRef = c)}
            showControls={true}
            controlsClass='controls'
            plusBtnClass={this.state.scale === metrics.scale.max ? 'inactive' : ''}
            minusBtnClass={this.state.scale === metrics.scale.min ? 'inactive' : ''}
            minScale={metrics.scale.min}
            maxScale={metrics.scale.max}
            scale={this.state.scale}
            translation={this.state.translation}
            onChange={this.handleTransform}
          >
            {
              ({ translation, scale }) => {
                // Translate first and then scale.  Otherwise, the scale would affect the translation.
                const transform = `translate(${translation.x}px, ${translation.y}px) scale(${scale})`;
                const map = (
                  <div className='mapWrapper'>
                    <div
                      style={{
                        transformOrigin: '0 0 ',
                        transform: transform,
                      }}
                      onDoubleClick={(e) => {
                        const delta = Math.abs(metrics.scale.max - metrics.scale.min) / 10;
                        const newScale = clamp(
                          metrics.scale.min,
                          this.state.scale + delta,
                          metrics.scale.max
                        );
                        const mousePos = this._mapInteractionRef.clientPosToTranslatedPos({ x: e.clientX, y: e.clientY });
                        this._mapInteractionRef.scaleFromPoint(newScale, mousePos);
                      }}
                    >
                      <Map
                        width={this.state.width}
                        height={this.state.height}
                        rotation={rotations[this.state.rotation]}
                        scale={this.state.scale}
                        setChartGeo={this.setChartGeo}
                        path={this.path}
                        map={this.map}
                        tooltip={this.state.tooltip}
                        openTooltip={this._openTooltip}
                        closeTooltip={this._closeTooltip}
                        stacks={this.stacks}
                        chart={this.state.chart}
                        data={this.data}
                        capitals={this.capitals}
                        recomputePaths={this._recomputePaths}
                      />
                      <svg
                        id='flier-stack-locations'
                        width={this.state.width}
                        height={this.state.height}
                        style={{ pointerEvents: 'none' }}
                      >
                        <Gradients />
                        {
                          this.stacks.ready ? (
                            <Stacks
                              data={this.data}
                              level={this.state.level}
                              scale={this.state.scale}
                              zooming={this.zooming}
                              openTooltip={this._openTooltip}
                              closeTooltip={this._closeTooltip}
                              setGeo={this.setChartGeo}
                            />
                          ) : ''
                        }
                      </svg>
                    </div>
                  </div>
                );

                this._recomputePaths = false;
                return map
              }
            }
          </MapInteraction>
          {
            this.state.tooltip ?
              <Tooltip
                dataKey={this.state.tooltip.key}
                keyFunctions={{
                  Group: this.groupByGroup,
                  Type: this.getCleanType,
                }}
                groupsToShow={this.groups}
                selectedGroupType={this.state.group}
                tooltip={this.state.tooltip}
                mapFeatures={this.map.states.features}
                globalScale={this.globalScale}
              /> :
              null
          }
        </div>
        <Chart
          width={this.state.width}
          dateRange={this.dateRange}
          stateExtent={this.yScales.state.domain()}
          selected={this.state.chart}
          setType={this.setChartType}
          setGeo={this.setChartGeo}
          states={this.data.state}
          group={this.groups[this.state.group].top}
          keyFunction={this.keyFunction}
          map={this.map}
          tooltip={this.state.tooltip}
          openTooltip={this._openTooltip}
          openDateTooltip={this._openDateTooltip}
          closeTooltip={this._closeTooltip}
        />
      </div>
    );
  }
}

export default App;
